import React from 'react';
import { Checkbox, Chip, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

export type MultiSelectFilterOption = { value: string; label: string };

type MultiSelectFilterProps = {
  label: string;
  emptyLabel: string;
  options: MultiSelectFilterOption[];
  value: string[];
  onChange: (value: string[]) => void;
  minWidth?: string;
  disabled?: boolean;
};

const MAX_CHIPS_SHOWN = 2;

export const MultiSelectFilter = ({
  label,
  emptyLabel,
  options,
  value,
  onChange,
  minWidth = '170px',
  disabled,
}: MultiSelectFilterProps) => {
  const { t } = useTranslation();
  const labelByValue = new Map(options.map((option) => [option.value, option.label]));

  const renderValue = (selected: string[]) => {
    if (!selected.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      );
    }
    // Past a couple of selections chips would stretch the field past the filter row.
    if (selected.length > MAX_CHIPS_SHOWN) {
      return <Typography variant="body2">{t('selectedCount', { count: selected.length })}</Typography>;
    }
    return (
      <Grid container gap={0.5} flexWrap="nowrap">
        {selected.map((selectedValue) => (
          <Chip key={selectedValue} size="small" label={labelByValue.get(selectedValue) ?? selectedValue} />
        ))}
      </Grid>
    );
  };

  return (
    <TextField
      select
      size="small"
      label={label}
      disabled={disabled}
      sx={{ minWidth }}
      value={value}
      onChange={(event) => onChange(event.target.value as unknown as string[])}
      SelectProps={{ multiple: true, renderValue: (selected) => renderValue(selected as string[]) }}>
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          <Checkbox size="small" checked={value.includes(option.value)} sx={{ p: 0, marginInlineEnd: 1 }} />
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
};
